import { CustomCode, isLegacyCustomCode } from '@superfunnel/types';
import Modal from '@superfunnel/ui-core/Modal';
import CodeInput from '@superfunnel/ui-core/v2/CodeInput';
import FieldTitle from '@superfunnel/ui-core/v2/FieldTitle';
import Input from '@superfunnel/ui-core/v2/Input';
import Switch from '@superfunnel/ui-core/v2/Switch';
import { createCustomCode, validateCustomCode } from '@superfunnel/utils';
import { useEffect, useState } from 'react';

interface CustomCodeEditorModalProps {
  customCode?: CustomCode;
  open: boolean;
  onClose: () => void;
  onSave: (code: CustomCode) => void;
  userId: string;
}

export default function CustomCodeEditorModal({
  customCode,
  open,
  onClose,
  onSave,
  userId,
}: CustomCodeEditorModalProps) {
  const isEditMode = !!customCode;

  const [name, setName] = useState('');
  const [headCode, setHeadCode] = useState('');
  const [headDelay, setHeadDelay] = useState<number>(0);
  const [bodyCode, setBodyCode] = useState('');
  const [bodyDelay, setBodyDelay] = useState<number>(0);
  const [enabled, setEnabled] = useState(true);
  const [errors, setErrors] = useState<string[]>([]);

  useEffect(() => {
    if (customCode) {
      setName(customCode.name);
      setEnabled(customCode.enabled);

      // Handle both legacy and new formats
      if (isLegacyCustomCode(customCode)) {
        // Legacy format: convert to new format for editing
        if (customCode.location === 'head') {
          setHeadCode(customCode.code || '');
          setHeadDelay(customCode.delay || 0);
          setBodyCode('');
          setBodyDelay(0);
        } else {
          setHeadCode('');
          setHeadDelay(0);
          setBodyCode(customCode.code || '');
          setBodyDelay(customCode.delay || 0);
        }
      } else {
        // New format
        setHeadCode(customCode.headCode || '');
        setHeadDelay(customCode.headDelay || 0);
        setBodyCode(customCode.bodyCode || '');
        setBodyDelay(customCode.bodyDelay || 0);
      }
    } else {
      // Reset for new code
      setName('');
      setHeadCode('');
      setHeadDelay(0);
      setBodyCode('');
      setBodyDelay(0);
      setEnabled(true);
    }
    setErrors([]);
  }, [customCode, open]);

  const handleSave = () => {
    let codeToSave: CustomCode;

    if (isEditMode && customCode) {
      // Update existing code - always save in new format
      // Remove legacy fields by destructuring them out
      const {
        code: _code,
        location: _location,
        delay: _delay,
        ...rest
      } = customCode;
      codeToSave = {
        ...rest,
        name,
        headCode: headCode || (null as any),
        headDelay: headDelay || (null as any),
        bodyCode: bodyCode || (null as any),
        bodyDelay: bodyDelay || (null as any),
        enabled,
        updatedAt: new Date() as any,
      };
    } else {
      // Create new code
      codeToSave = createCustomCode(name, userId, {
        headCode: headCode || (null as any),
        headDelay: headDelay || (null as any),
        bodyCode: bodyCode || (null as any),
        bodyDelay: bodyDelay || (null as any),
        enabled,
      });
    }

    const validation = validateCustomCode(codeToSave);
    if (!validation.valid) {
      setErrors(validation.errors);
      return;
    }

    onSave(codeToSave);
    onClose();
  };

  return (
    <Modal
      title={isEditMode ? 'Edit Custom Code' : 'Add Custom Code'}
      open={open}
      onClose={onClose}
      actions={[
        {
          type: 'secondary',
          title: 'Cancel',
          onClick: onClose,
        },
        {
          type: 'primary',
          title: isEditMode ? 'Save Changes' : 'Add Code',
          onClick: handleSave,
        },
      ]}
    >
      <div>
        <div className="space-y-4">
          {errors.length > 0 && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="flex">
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">
                    Please fix the following errors:
                  </h3>
                  <div className="mt-2 text-sm text-red-700">
                    <ul className="list-disc space-y-1 pl-5">
                      {errors.map((error) => (
                        <li key={error}>{error}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          <Input
            title="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Google Analytics"
            required
          />

          <div className="flex items-center gap-2">
            <Switch
              id="enabled"
              checked={enabled}
              onCheckedChange={setEnabled}
            />
            <FieldTitle htmlFor="enabled" title="Enabled" />
          </div>

          <div className="border-t border-gray-200 pt-4">
            <h4 className="mb-3 text-sm font-medium text-gray-700">
              Head Code{' '}
              <span className="font-normal text-gray-500">(optional)</span>
            </h4>
            <CodeInput
              title={'Code to inject in <head>'}
              value={headCode}
              onChange={setHeadCode}
              language={'javascript'}
              inlineLines={4}
              placeholder={'<!-- Add code for <head> here -->'}
            />
            <div className="mt-2">
              <Input
                title="Head Delay (ms)"
                type="number"
                value={headDelay.toString()}
                onChange={(e) => setHeadDelay(parseInt(e.target.value) || 0)}
                placeholder="0"
                min={0}
              />
            </div>
          </div>

          <div className="border-t border-gray-200 pt-4">
            <h4 className="mb-3 text-sm font-medium text-gray-700">
              Body Code{' '}
              <span className="font-normal text-gray-500">(optional)</span>
            </h4>
            <CodeInput
              title={'Code to inject in <body>'}
              value={bodyCode}
              onChange={setBodyCode}
              language={'javascript'}
              inlineLines={4}
              placeholder={'<!-- Add code for <body> here -->'}
            />
            <div className="mt-2">
              <Input
                title="Body Delay (ms)"
                type="number"
                value={bodyDelay.toString()}
                onChange={(e) => setBodyDelay(parseInt(e.target.value) || 0)}
                placeholder="0"
                min={0}
              />
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
